import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { modelColor } from '@/components/canvas/library/library-types'
import { Toggle } from '@/components/canvas/library/library-cards'
import type { CatalogModel } from '@/lib/api/models'

export const Route = createFileRoute('/test-modelcard-design')({
  component: ModelCardDesignPage,
})

// ─── Mock Data ───────────────────────────────────────────────────────────────

const MOCK_MODELS: CatalogModel[] = [
  {
    id: 'mock-flux-schnell',
    name: 'FLUX.1 Schnell',
    endpointId: 'black-forest-labs/flux-schnell',
    description: 'Fast image generation model',
    category: 'text-to-image',
    nodeType: 'image',
    modelFamily: 'FLUX',
    purpose: 'create',
    featuredScore: 95,
    capabilities: ['fast', 'high-quality'],
    thumbnailUrl: 'https://picsum.photos/seed/flux-schnell/400/300',
    provider: { id: 'p1', key: 'bfl', name: 'Black Forest Labs', enabled: true },
  },
  {
    id: 'mock-runway-gen3',
    name: 'Runway Gen-3 Alpha Turbo',
    endpointId: 'runway/gen3-alpha-turbo',
    description: 'Video generation from text or image',
    category: 'text-to-video',
    nodeType: 'video',
    modelFamily: 'Gen-3',
    purpose: 'create',
    featuredScore: 90,
    capabilities: ['turbo', 'high-quality'],
    thumbnailUrl: 'https://picsum.photos/seed/runway-gen3/400/300',
    provider: { id: 'p2', key: 'runway', name: 'Runway', enabled: true },
  },
  {
    id: 'mock-no-thumb',
    name: 'Stable Audio 2.0',
    endpointId: 'stability/stable-audio-2',
    description: 'Audio and music generation',
    category: 'text-to-audio',
    nodeType: 'sound',
    modelFamily: 'Stable Audio',
    purpose: 'create',
    featuredScore: 80,
    capabilities: ['music', 'sfx'],
    provider: { id: 'p3', key: 'stability', name: 'Stability AI', enabled: true },
  },
  {
    id: 'mock-long-name',
    name: 'FLUX.1 [dev] Canny with LoRAs Ultra Extended Edition Pro',
    endpointId: 'bfl/flux-canny-lora',
    description: 'Edge-guided image generation with LoRA support',
    category: 'image-to-image',
    nodeType: 'image',
    modelFamily: 'FLUX',
    purpose: 'tools',
    featuredScore: 70,
    capabilities: ['canny', 'lora'],
    thumbnailUrl: 'https://picsum.photos/seed/flux-canny/400/300',
    provider: { id: 'p1', key: 'bfl', name: 'Black Forest Labs', enabled: true },
  },
  {
    id: 'mock-enhance',
    name: 'Real-ESRGAN x4',
    endpointId: 'esrgan/x4',
    description: 'Image upscaling and enhancement',
    category: 'image-upscale',
    nodeType: 'image',
    modelFamily: 'ESRGAN',
    purpose: 'enhance',
    featuredScore: 85,
    capabilities: ['upscale', '4x'],
    thumbnailUrl: 'https://picsum.photos/seed/esrgan/400/300',
    provider: { id: 'p4', key: 'replicate', name: 'Replicate', enabled: true },
  },
]

// ─── Shared Helpers ──────────────────────────────────────────────────────────

function buildTags(model: CatalogModel): string[] {
  const tags: string[] = []
  if (model.purpose) tags.push(model.purpose)
  if (model.nodeType) tags.push(model.nodeType)
  else if (model.category) tags.push(model.category)
  if (model.capabilities?.[0] && tags.length < 2 && !tags.includes(model.capabilities[0])) tags.push(model.capabilities[0])
  return tags
}

function TagPills({ tags, variant = 'dark' }: { tags: string[]; variant?: 'dark' | 'light' }) {
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className={cn(
            'inline-flex items-center h-5 px-2 rounded-md text-[11px] font-medium whitespace-nowrap',
            variant === 'dark'
              ? 'bg-white/20 text-white backdrop-blur-sm'
              : 'bg-background/80 text-foreground border border-border/50',
          )}
        >
          {tag}
        </span>
      ))}
    </div>
  )
}

// ─── Variant 1: Frosted Glass ────────────────────────────────────────────────

function FrostedGlassCard({ model }: { model: CatalogModel }) {
  const [inLibrary, setInLibrary] = useState(false)
  const bg = modelColor(model.id)
  const tags = buildTags(model)
  const authorName = model.modelFamily ?? model.provider?.name ?? 'Author'

  return (
    <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden group cursor-pointer">
      {/* Background: image or solid color */}
      {model.thumbnailUrl ? (
        <img
          src={model.thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover scale-110"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: bg }} />
      )}

      {/* Full frost overlay */}
      <div className="absolute inset-0 backdrop-blur-2xl bg-black/40" />

      {/* Content — top-left aligned */}
      <div className="relative h-full flex flex-col justify-between p-4">
        <div className="space-y-3">
          <p className="text-base font-bold text-white leading-snug line-clamp-3">{model.name}</p>
          <TagPills tags={tags} variant="dark" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/60 truncate max-w-[120px]">{authorName}</span>
          <Toggle checked={inLibrary} onChange={() => setInLibrary(!inLibrary)} />
        </div>
      </div>
    </div>
  )
}

// ─── Variant 2: Gradient Overlay ─────────────────────────────────────────────

function GradientOverlayCard({ model }: { model: CatalogModel }) {
  const [inLibrary, setInLibrary] = useState(false)
  const bg = modelColor(model.id)
  const tags = buildTags(model)
  const authorName = model.modelFamily ?? model.provider?.name ?? 'Author'

  return (
    <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden group cursor-pointer">
      {/* Background image or solid color */}
      {model.thumbnailUrl ? (
        <img
          src={model.thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: bg }} />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

      {/* Content pinned to bottom */}
      <div className="relative h-full flex flex-col justify-end p-3 space-y-2">
        <p className="text-sm font-semibold text-white leading-tight line-clamp-2">{model.name}</p>
        <TagPills tags={tags} variant="dark" />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-white/60 truncate max-w-[100px]">{authorName}</span>
          <Toggle checked={inLibrary} onChange={() => setInLibrary(!inLibrary)} />
        </div>
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

function ModelCardDesignPage() {
  const variants = [
    { name: 'Frosted Glass', description: 'Full frost blur + text top-left', Component: FrostedGlassCard },
    { name: 'Gradient Overlay', description: 'Sharp image + gradient fade to black', Component: GradientOverlayCard },
  ]

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto space-y-12">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Model Card Design Options</h1>
          <p className="text-sm text-muted-foreground mt-1">Compare different card styles before choosing one for the library</p>
        </div>

        {variants.map(({ name, description, Component }) => (
          <section key={name} className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">{name}</h2>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {MOCK_MODELS.map((model) => (
                <Component key={model.id} model={model} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
